<template>
  <ODialog
    ref="dialog"
    :title="$t('components.upload.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.delete'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Upload } from '~/_api/models/upload'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref()
const upload = ref<Upload | null>(null)

const info = computed(() => {
  return $i18n.t('components.upload.actions.delete.info')
})

const open = (uploadItem: Upload) => {
  upload.value = uploadItem

  if (!uploadItem) {
    return false
  }

  dialog.value.open()
}

const confirm = async () => {
  if (!upload.value) return

  emit('update:loading', true)

  try {
    const response = await $api.upload.delete(upload.value)

    if (response.success) {
      emit('success')

      $notification.toast({
        title: $i18n.t('components.upload.actions.delete.success'),
        text: $i18n.t('components.upload.actions.delete.successInfo'),
        variant: 'success',
      })

      emit('update:loading', false)

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.upload.actions.delete.errorInfo'),
    })
  }

  emit('update:loading', false)
}

defineExpose({
  open,
})
</script>
